import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { mergeStyles } from '@cian/utils';
import { throttle } from 'lodash';

import { getCoords } from '../../../../browser/utils/dom';
import { scrollToElement } from '../../../utils/navigation';

import { MainFilters } from './mainFilters';
import { AdvancedFilters } from './advancedFilters';

import * as styles from './filters.css';
import { trackAdvancedModalOpenEvent } from './tracking';

interface IFiltersProps {
  isNewAdvancedFilters: boolean;
  // tslint:disable-next-line:no-any
  onAdvancedModalOpen(): any;
  // tslint:disable-next-line:no-any
  onSearchButtonClick(): any;
  onFiltersScrollUp?(): void;
  onFiltersScrollDown?(): void;
  setFiltersPosition?(position: EPosition): void;
}

interface IFiltersState {
  minHeight: number;
  position: EPosition;
  withoutTransition: boolean;
  isAdvancedFiltersOpened: boolean;
}

export enum EPosition {
  default,
  Fixed,
  FixedHidden,
}

export class Filters extends React.Component<IFiltersProps, IFiltersState> {
  private currentScrollTop: number;
  private filtersOffsetTop: number;
  private filtersDiv: HTMLDivElement | null = null;

  public constructor(props: IFiltersProps) {
    super(props);

    this.state = {
      minHeight: 0,
      position: EPosition.default,
      withoutTransition: false,
      isAdvancedFiltersOpened: false,
    };
  }

  public static getDerivedStateFromProps(props: IFiltersProps) {
    if (props.isNewAdvancedFilters) {
      return null;
    }

    return {
      isAdvancedFiltersOpened: false,
    };
  }

  public componentDidUpdate(prevProps: IFiltersProps) {
    if (this.state.isAdvancedFiltersOpened && prevProps.isNewAdvancedFilters) {
      document.body.classList.add(styles['body-overflow']);
    } else {
      document.body.classList.remove(styles['body-overflow']);
    }
  }

  public componentDidMount() {
    this.initializeStickyFilters();
  }

  public componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);

    this.handleScroll.cancel();
  }

  public render() {
    const { position, withoutTransition, minHeight } = this.state;

    return (
      <div
        {...mergeStyles(
          styles['filters'],
          { minHeight: `${minHeight}px` },
          (position === EPosition.Fixed || position === EPosition.FixedHidden) && styles['fixed'],
          position === EPosition.FixedHidden && styles['hidden'],
          withoutTransition && styles['notransition'],
        )}
      >
        <div {...mergeStyles(styles['filters-container'], styles['main-filters-container'])}>
          {this.state.isAdvancedFiltersOpened && (
            <div className={styles['back']} onClick={this.handleToggleAdvancedFilters} />
          )}
          <div
            ref={(ref: HTMLDivElement | null) => {
              this.filtersDiv = ref;
            }}
            {...mergeStyles(
              styles['main-filters-content'],
              this.state.isAdvancedFiltersOpened && styles['main-filter-content-active'],
            )}
          >
            <MainFilters
              isAdvancedFiltersOpened={this.state.isAdvancedFiltersOpened}
              onSearchButtonClick={this.handleSearchButtonClick}
              onAdvancedFiltersButtonClick={this.handleAdvancedFiltersButtonClick}
            />
          </div>
          <CSSTransition
            in={this.state.isAdvancedFiltersOpened}
            timeout={500}
            unmountOnExit
            classNames="filters-transition"
          >
            <div {...mergeStyles(styles['filters-container'], styles['advanced-filters-container'])}>
              <AdvancedFilters onSearchButtonClick={this.handleSearchButtonClick} />
            </div>
          </CSSTransition>
        </div>
      </div>
    );
  }

  private handleAdvancedFiltersButtonClick = () => {
    if (this.props.isNewAdvancedFilters) {
      this.handleToggleAdvancedFilters();
    } else {
      this.props.onAdvancedModalOpen();
    }
  };

  private handleToggleAdvancedFilters = () => {
    this.setState(
      {
        isAdvancedFiltersOpened: !this.state.isAdvancedFiltersOpened,
      },
      () => {
        if (this.state.isAdvancedFiltersOpened) {
          trackAdvancedModalOpenEvent();
        }
      },
    );
  };

  private handleSearchButtonClick = () => {
    if (this.state.isAdvancedFiltersOpened) {
      this.setState({
        isAdvancedFiltersOpened: false,
      });
    }

    if (this.state.position === EPosition.Fixed) {
      scrollToElement(null);
    }

    this.props.onSearchButtonClick();
  };

  private initializeStickyFilters = () => {
    if (!this.filtersDiv) {
      return;
    }

    const coords = getCoords(this.filtersDiv);

    this.filtersOffsetTop = coords.top;
    this.currentScrollTop = document.body.scrollTop;

    window.addEventListener('scroll', this.handleScroll);
  };

  private handleScroll = throttle(() => {
    if (!this.filtersDiv) {
      return;
    }

    const filtersHeight = Math.round(this.filtersDiv.getBoundingClientRect().height);

    // Если изменилась высота фильтров, то не обрабатываем сработавший скролл
    if (filtersHeight !== this.state.minHeight) {
      this.setState({
        minHeight: filtersHeight,
      });

      return;
    }

    const newScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    const areFiltersVisible = newScrollTop <= this.filtersOffsetTop + filtersHeight;
    const scrollingDown = newScrollTop > this.currentScrollTop;
    const scrollingUp = newScrollTop < this.currentScrollTop;
    this.currentScrollTop = newScrollTop;

    const switchPosition = (newPosition: EPosition) => {
      switch (newPosition) {
        case EPosition.FixedHidden:
          this.setState({
            withoutTransition: this.state.position === EPosition.default,
            position: EPosition.FixedHidden,
          });
          break;

        case EPosition.Fixed:
          this.setState({
            position: EPosition.Fixed,
            withoutTransition: false,
          });
          break;

        default:
          this.setState({
            position: EPosition.default,
          });
      }
      this.props.setFiltersPosition?.(newPosition);
    };

    if (scrollingUp) {
      if (newScrollTop <= this.filtersOffsetTop) {
        switchPosition(EPosition.default);
      } else {
        switchPosition(EPosition.Fixed);

        if (this.props.onFiltersScrollUp) {
          this.props.onFiltersScrollUp();
        }
      }
    } else if (scrollingDown) {
      if (!areFiltersVisible) {
        switchPosition(EPosition.FixedHidden);
      }
      if (this.props.onFiltersScrollDown) {
        this.props.onFiltersScrollDown();
      }
    }
  }, 50);
}
